import React, { useState, useMemo } from 'react';
import Select from '../../components/Form/Select';
import Input from '../../components/Form/Input';
import Button from '../../components/Form/Button';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, ErrorState } from '../../components/Common';
import TimesheetGrid from './TimesheetGrid';
import OffCrewSection from './OffCrewSection';
import { useBranches } from '../../hooks/useBranches';
import { useTimesheetState, ALL_VESSELS } from './useTimesheetState';
import { currentMonthValue, countWorkedDays } from './timesheetUtils';
import { printTimesheet } from './timesheetPrint';

const Timesheet: React.FC = () => {
  const [vesselId, setVesselId] = useState('');
  const [month, setMonth] = useState(currentMonthValue());

  const lookupQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: branches = [] } = useBranches(lookupQO);

  const {
    employeesLoading, error, refetch,
    selectedBranch, vesselEmployees,
    rows, handleRowChange,
    offCrewRows, handleOffCrewChange, addOffCrewRow, removeOffCrewRow,
    save, isSaving,
  } = useTimesheetState(vesselId, month, branches as any[]);

  const vesselOptions = useMemo(() => [
    { value: '', label: 'Select vessel/headquarters...' },
    { value: ALL_VESSELS, label: 'All Employees' },
    ...(branches as any[]).map((b: any) => ({
      value: b.documentId || String(b.id),
      label: `${b.name}${b.type ? ` (${b.type})` : ''}`,
    })),
  ], [branches]);

  const totalWorked = useMemo(
    () => vesselEmployees.reduce((sum: number, emp: any) => {
      const key = emp.documentId || String(emp.id);
      return sum + countWorkedDays(rows[key]?.days);
    }, 0),
    [vesselEmployees, rows]
  );

  const handlePrint = () => {
    if (!vesselId) return;
    printTimesheet({
      vesselName: vesselId === ALL_VESSELS ? 'All Vessels' : (selectedBranch?.name || '-'),
      month,
      employees: vesselEmployees,
      rows,
      offCrewRows: vesselId === ALL_VESSELS ? [] : offCrewRows,
    });
  };

  if (error) return <ErrorState message="Error loading employees." onRetry={refetch} entityName="timesheet" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Time Sheet"
        description="Track worked days per employee on each vessel — تعيين and رفت days count as half a day"
      />

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-72">
            <Select label="Vessel / Headquarters" value={vesselId} onChange={(e) => setVesselId(e.target.value)} options={vesselOptions} />
          </div>
          <div className="w-full sm:w-48">
            <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div className="flex-1" />
          {vesselId && (
            <div className="flex gap-3 pb-1">
              <div className="text-center px-4 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-lg font-bold text-gray-900 leading-tight">{vesselEmployees.length}</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Employees</div>
              </div>
              <div className="text-center px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-lg font-bold text-emerald-700 leading-tight">{totalWorked}</div>
                <div className="text-[11px] text-emerald-600 uppercase tracking-wide">Worked Days</div>
              </div>
            </div>
          )}
          <Button variant="secondary" onClick={handlePrint} disabled={!vesselId}>
            Print
          </Button>
          <Button variant="primary" onClick={save} loading={isSaving} disabled={!vesselId}>
            Save Timesheet
          </Button>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600 mt-4 pt-3 border-t border-gray-100">
          <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-emerald-500 text-white border border-emerald-600 font-bold text-xs">✓</span> Worked (1 day)</span>
          <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-amber-400 text-white border border-amber-500 font-bold text-xs">½</span> تعيين / رفت (half day)</span>
          <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white border border-gray-200"></span> Not worked (0)</span>
          <span className="text-gray-400">Click a day to cycle: worked → half → off</span>
        </div>
      </div>

      {employeesLoading ? (
        <TableSkeleton />
      ) : vesselId ? (
        <>
          <TimesheetGrid employees={vesselEmployees} month={month} rows={rows} onChange={handleRowChange} />
          {vesselId !== ALL_VESSELS && (
            <OffCrewSection
              month={month}
              rows={offCrewRows}
              onChange={handleOffCrewChange}
              onAdd={addOffCrewRow}
              onRemove={removeOffCrewRow}
            />
          )}
        </>
      ) : (
        <div className="bg-white shadow rounded-lg text-center py-12">
          <p className="text-gray-500">Select a vessel/headquarters to show its employees</p>
        </div>
      )}
    </div>
  );
};

export default Timesheet;
